import { usePage } from '@inertiajs/vue3'
import api from '@/api'
import { connectFirebase, firebaseToken, preloadFirebase } from '@/utils/firebaseAuth'
import { googleRedirect } from '@/store/user.module.js'

async function signedInEmail({ rootState, dispatch }) {
    if (!rootState.user.isLoggedIn) {
        await dispatch('user/getUser', null, { root: true })
    }
    return rootState.user.auth.email
}

const FavoritesModule = {
    state: () => ({
        all: [],
        page: 0,
        lastPage: 1,
        total: 0,
        status: 'idle', // idle | loading | connect | ready | error
        loadingMore: false,
        liked: {}, // song id → liked, from this visit; wins over song.is_liked
    }),
    mutations: {
        SET_STATUS(state, status) {
            state.status = status
        },
        SET_LOADING_MORE(state, loading) {
            state.loadingMore = loading
        },
        SET_PAGE(state, { current_page, last_page, total, data }) {
            state.all = current_page === 1
                ? data
                : [...state.all, ...data.filter((song) => !state.all.some((s) => s.id === song.id))]
            // Everything this list returns is liked, whatever song.is_liked says
            state.liked = { ...state.liked, ...Object.fromEntries(data.map((song) => [song.id, true])) }
            state.page = current_page
            state.lastPage = last_page
            state.total = total
            state.status = 'ready'
        },
        SET_LIKED(state, { song, liked }) {
            state.liked = { ...state.liked, [song.id]: liked }
            const had = state.all.some((s) => s.id === song.id)
            if (!liked && had) {
                state.all = state.all.filter((s) => s.id !== song.id)
                state.total = Math.max(0, state.total - 1)
            } else if (liked && !had && state.status === 'ready') {
                state.all = [song, ...state.all]
                state.total += 1
            }
        },
    },
    actions: {
        // Page 1 replaces the list; later pages add to it
        async loadFavorites({ commit, dispatch, rootState, state }, page = 1) {
            if (page === 1) commit('SET_STATUS', 'loading')
            else commit('SET_LOADING_MORE', true)
            try {
                const email = await signedInEmail({ rootState, dispatch })
                const token = await firebaseToken(email)
                if (!token) {
                    commit('SET_STATUS', 'connect')
                    return
                }
                const { data } = await api.FavoriteApi.all(page, token)
                commit('SET_PAGE', data)
            } catch (error) {
                if (page === 1) commit('SET_STATUS', 'error')
                else dispatch('notification/showNotification', { message: "Couldn't load more songs", type: 'error' }, { root: true })
            } finally {
                commit('SET_LOADING_MORE', false)
            }
        },
        async connectFavorites({ dispatch, rootState }) {
            const connected = await dispatch('connect', rootState.user.auth.email)
            if (connected) await dispatch('loadFavorites')
        },
        // Returns a token, or null with the reason shown to the user
        async connect({ dispatch }, email) {
            try {
                return await connectFirebase(email)
            } catch (error) {
                const message = {
                    'wrong-account': `Please choose ${email}, the account you're logged in with.`,
                    'auth/popup-blocked': 'Allow pop-ups for Heesify to save your likes.',
                }[error.code]
                if (message) {
                    dispatch('notification/showNotification', { message, type: 'error' }, { root: true })
                }
                return null
            }
        },
        // The heart: like or unlike, showing the change straight away
        async toggleFavorite({ commit, dispatch, getters, rootState }, song) {
            if (!rootState.user.isLoggedIn && !window.localStorage.getItem('_token') && !usePage().props.loggedIn) {
                return dispatch('user/login', null, { root: true })
            }
            const liked = !getters.isLiked(song)
            let token = null
            try {
                const email = await signedInEmail({ rootState, dispatch })
                token = (await firebaseToken(email)) || (await dispatch('connect', email))
            } catch (error) {
                return googleRedirect()
            }
            if (!token) return

            commit('SET_LIKED', { song, liked })
            try {
                if (liked) await api.FavoriteApi.add(song.id, token)
                else await api.FavoriteApi.delete(song.id, token)
            } catch (error) {
                commit('SET_LIKED', { song, liked: !liked })
                dispatch('notification/showNotification', { message: "Couldn't update your likes. Please try again.", type: 'error' }, { root: true })
            }
        },
        preload() {
            preloadFirebase().catch(() => {})
        },
    },
    getters: {
        isLiked: (state) => (song) => state.liked[song.id] ?? !!song.is_liked,
        hasMore: (state) => state.page < state.lastPage,
    },
    namespaced: true,
};
export default FavoritesModule;
